<template>
    <Select
        v-if="filter.type === 'dropdown' || filter.type === 'multi-select' || filter.type === 'boolean'"
        v-model="computedActive"
        :dir="$dir.value"
        :multiple="isMultiple"
    >
        <SelectTrigger class="w-45">
            <SelectValue>
                <template #default>
                    <Icon v-if="filter.icon" :name="filter.icon" class="shrink-0" />
                    {{ $t(`category.filters.${filter.name}`, filter.name) }}
                    <span
                        v-if="computedActive && (Array.isArray(computedActive) ? computedActive.length : computedActive)"
                        class="opacity-50 text-xs truncate"
                    >
                        ({{ Array.isArray(computedActive) ? computedActive.join(",") : computedActive }})
                    </span>
                </template>
            </SelectValue>
        </SelectTrigger>
        <SelectContent>
            <SelectGroup>
                <SelectItem v-for="(value, key) in filterValues" :key :value="value">
                    {{ $t(`category.filters.${(value as any).name ?? value}`, (value as any).name ?? value) }}
                </SelectItem>
            </SelectGroup>
        </SelectContent>
    </Select>
</template>

<script lang="ts" setup>
import type { APICategoryAttributes } from "~~/shared/types/category";
import type { ActiveFilterItem } from "./main.vue";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Props {
    filter: APICategoryAttributes;
    active?: ActiveFilterItem;
}

interface ValueObject {
    name: string;
    value?: string;
}

type ValueObjectType = ValueObject | string | string[] | boolean | null;

const emits = defineEmits(["update:filter"]);

const props = defineProps<Props>();

const { t } = useI18n();

const computedActive = computed({
    get() {
        return props.active?.value;
    },
    set(value: ValueObjectType) {
        emits("update:filter", {
            name: props.filter.name,
            value:
                typeof value === "object" && value !== null && !Array.isArray(value) && "value" in value
                    ? value?.value === undefined
                        ? undefined
                        : String(value.value)
                    : value,
        });
    },
});

const filterValues = computed(() => {
    if (props.filter.type === "multi-select" || props.filter.type === "dropdown") return props.filter?.values;
    else if (props.filter.type === "boolean")
        return [
            { name: t("category.filters.all"), value: undefined },
            { name: t("category.filters.true"), value: 1 },
            { name: t("category.filters.false"), value: 0 },
        ];
    return props.filter.values;
});

const isMultiple = computed<boolean>(() => {
    if (props.filter.type === "multi-select" || props.filter.type === "dropdown") return true;
    else if (props.filter.type === "boolean") return false;
    return false;
});
</script>

<style></style>
